import httpService from "../../services/httpService";
import { ApiEndpoints } from "../../constants/apiEndpoints";

export type AnnouncementActionType =
  | "internal_route"
  | "external_url"
  | "song"
  | "training"
  | "none";

export interface Announcement {
  id: string;
  title: string;
  description: string;
  image_url: string | null;
  button_text: string | null;
  action_type: AnnouncementActionType;
  action_value: string | null;
  category: string;
  priority: number;
  is_active: boolean;
  start_date: string;
  end_date: string | null;
  language: string;
  created_at: string;
  updated_at: string;
}

export interface AnnouncementCreateRequest {
  title: string;
  description: string;
  image_url?: string | null;
  button_text?: string | null;
  action_type?: AnnouncementActionType;
  action_value?: string | null;
  category?: string;
  priority?: number;
  is_active?: boolean;
  start_date: string;
  end_date?: string | null;
  language?: string;
}

export type AnnouncementUpdateRequest = Partial<AnnouncementCreateRequest>;

function announcementPath(id?: string) {
  const base = ApiEndpoints.ADMIN_ANNOUNCEMENTS.replace(/\/$/, "");
  return id ? `${base}/${encodeURIComponent(id)}` : base;
}

function publicAnnouncementPath() {
  return ApiEndpoints.ANNOUNCEMENTS.replace(/\/$/, "");
}

function asCollection(payload: unknown): Announcement[] {
  if (Array.isArray(payload)) return payload as Announcement[];
  if (!payload || typeof payload !== "object") return [];
  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.items)) return record.items as Announcement[];
  if (Array.isArray(record.announcements)) {
    return record.announcements as Announcement[];
  }
  return [];
}

export async function listAdminAnnouncements(): Promise<Announcement[]> {
  const response = await httpService.get<
    Announcement[] | { items?: Announcement[]; announcements?: Announcement[] }
  >(announcementPath());
  return asCollection(response.data);
}

export async function listPublicAnnouncements(
  language?: string
): Promise<Announcement[]> {
  const response = await httpService.get<
    Announcement[] | { items?: Announcement[]; announcements?: Announcement[] }
  >(publicAnnouncementPath(), {
    params: language ? { language } : undefined,
  });
  return asCollection(response.data);
}

export async function createAnnouncement(
  body: AnnouncementCreateRequest
): Promise<Announcement> {
  const response = await httpService.post<Announcement>(
    announcementPath(),
    body
  );
  return response.data;
}

export async function updateAnnouncement(
  announcementId: string,
  body: AnnouncementUpdateRequest
): Promise<Announcement> {
  const response = await httpService.put<Announcement>(
    announcementPath(announcementId),
    body
  );
  return response.data;
}

export async function deleteAnnouncement(
  announcementId: string
): Promise<void> {
  await httpService.delete(announcementPath(announcementId));
}
